import { Link } from 'react-router';
import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

// Reusable doc primitives, match the visual language of the other
// /docs/* pages. Body copy is English-only by design; the surrounding
// chrome (nav label, breadcrumb) goes through i18n.

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-gray-800 dark:bg-gray-800 dark:text-gray-200">
      {children}
    </code>
  );
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 text-xs align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'model', label: 'Fleet model', level: 1 },
  { id: 'setup-flow', label: 'Adding an agent', level: 1 },
  { id: 'quickstart', label: 'Quick start' },
  { id: 'install', label: 'Install', level: 1 },
  { id: 'enroll', label: 'Enrol the agent', level: 1 },
  { id: 'groups', label: 'Groups & inputs' },
  { id: 'config-version', label: 'Config versions', level: 1 },
  { id: 'parsing', label: 'Parsing' },
  { id: 'host-monitoring', label: 'Host monitoring' },
  { id: 'host-metrics', label: 'Host metrics', level: 1 },
  { id: 'process-monitoring', label: 'Process monitoring', level: 1 },
  { id: 'execve-preset', label: 'Auditd execve preset', level: 1 },
  { id: 'protocol', label: 'Control-plane protocol' },
  { id: 'enrollment-api', label: 'Enrolment', level: 1 },
  { id: 'config-poll', label: 'Config polling', level: 1 },
  { id: 'heartbeats', label: 'Heartbeats', level: 1 },
  { id: 'actions', label: 'Remote actions', level: 1 },
  { id: 'upgrades', label: 'Remote upgrades' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
];

export function AgentsFleetPage() {
  useDocumentMeta({
    title: 'Agents & fleet: LogPulse Docs',
    description: 'How LogPulse agents enrol, receive config, send heartbeats, and apply remote upgrades.',
  });

  const t = useT();

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
            {t('docs.nav.dataProcessing')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Agents &amp; fleet
          </h1>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            The LogPulse agent is a lightweight Go control plane wrapped around Vector. Agents enrol once, pull
            their configuration from LogPulse, send periodic heartbeats, and pick up remote actions, including
            self-upgrades, without ever exposing an inbound port.
          </p>
        </div>

        {/* OVERVIEW */}
        <Section id="overview" title="Overview">
          <P>
            Every host or container that ships logs into LogPulse runs the <Code>logpulse-agent</Code> binary.
            The agent doesn&apos;t generate Vector configuration locally. It asks the LogPulse API for a config
            that was assembled from the group&apos;s inputs in the UI, writes it to disk, and runs Vector against
            it. All operator changes happen in the dashboard; agents apply them on their next poll.
          </P>

          <SubSection id="model" title="Fleet model">
            <P>
              Agents are organised into <strong>groups</strong>. A group has a name, a slug, a set of inputs
              (syslog listener, journald reader, Kafka consumer, …) and a monotonically increasing{' '}
              <Code>config_version</Code>. Every host that enrols into a group receives the same Vector
              configuration. That&apos;s the unit of fleet-wide change.
            </P>
            <Table
              headers={['Concept', 'What it is', 'Where it lives']}
              rows={[
                ['Group', 'A logical bundle of hosts that share a Vector config', 'agent_groups table; Data processing → Agents in the app'],
                ['Input', 'One Vector source (syslog, file, journald, kafka, …) attached to a group', 'agent_inputs table'],
                ['Agent', 'A single host registered with a bearer token (AGT_…)', 'agent_registrations table'],
                ['Enrolment token', 'Short-lived secret that lets new hosts join a group', 'agent_enrollment_tokens table'],
                ['Action', 'A queued instruction for an agent (e.g. upgrade_agent)', 'agent_actions table'],
              ]}
            />
            <Callout type="info">
              Group membership is mutable. Move an agent to a different group and its{' '}
              <Code>applied_config_version</Code> resets to 0. The next poll delivers the new group&apos;s
              config without a reinstall.
            </Callout>
          </SubSection>

          <SubSection id="setup-flow" title="Adding an agent">
            <P>
              Agents live under <strong>Configure → Data processing → Agents</strong>. The page opens with four
              counts (online, offline, not up to date, waiting for config); click one to filter the list. To add a
              server, click <strong>Add agent</strong>. A wizard walks you through it:
            </P>
            <Table
              headers={['Step', 'What happens']}
              rows={[
                ['1. Group', 'Add the server to an existing group, or create a new one (your first group if you have none).'],
                ['2. Install', 'Create an enrolment token and copy the install command. Servers that join show up live; continue when one appears, or skip waiting.'],
                ['3. Collect', 'Pick what to collect: one or more inputs, or a template for a known device. Nothing changes on the servers until you click Apply configuration.'],
                ['4. Verify', 'Watch each server pick up the new configuration, then open a search for its data in a new tab. Templates show what to configure on the device itself.'],
              ]}
            />
            <P>
              Existing groups are managed from the <em>Groups</em> tab: each group reads as Collect, Install,
              Agents and Parse, and the generated Vector configuration is one click away under{' '}
              <em>Config (YAML)</em>.
            </P>
          </SubSection>
        </Section>

        {/* QUICKSTART */}
        <Section id="quickstart" title="Quick start">
          <SubSection id="install" title="Install the agent">
            <P>
              The agent runs on Linux (systemd) and Kubernetes (DaemonSet). For a Linux host, the install
              one-liner downloads the binary, verifies the SHA-256, installs a systemd unit, and waits for an
              enrolment token to start the agent:
            </P>
            <CodeBlock title="bash">{`curl -fsSL https://get.logpulse.io | sudo sh`}</CodeBlock>
            <P>
              Pin a specific release by setting <Code>LOGPULSE_VERSION</Code> before piping:
            </P>
            <CodeBlock title="bash">{`curl -fsSL https://get.logpulse.io | sudo LOGPULSE_VERSION=v0.1.0 sh`}</CodeBlock>
            <P>The installer creates these paths:</P>
            <Table
              headers={['Path', 'Purpose']}
              rows={[
                [<Code key="b">/usr/bin/logpulse-agent</Code>, 'Binary symlink (versioned target under /opt)'],
                [<Code key="c">/etc/logpulse-agent/</Code>, 'Static config + bootstrap token'],
                [<Code key="d">/var/lib/logpulse-agent/</Code>, 'State: applied config, Vector data-dir'],
                [<Code key="e">/var/log/logpulse-agent/</Code>, 'Agent + Vector logs'],
              ]}
            />
          </SubSection>

          <SubSection id="enroll" title="Enrol the agent">
            <P>
              In the dashboard, open <strong>Data processing → Agents</strong>, click <em>Add agent</em>, choose a
              group and click <em>Create enrollment token</em> (for an existing group you can also use its{' '}
              <em>Install</em> step). The token (prefix <Code>EBT_</Code>) is shown once. Copy it, then
              run on the host:
            </P>
            <CodeBlock title="bash">{`sudo logpulse-agent enrol \\
  --api-url https://api.logpulse.io \\
  --token EBT_<bootstrap-token>`}</CodeBlock>
            <P>
              The agent posts its hostname, platform, and version to LogPulse and receives back a long-lived{' '}
              <Code>AGT_</Code> token. The bootstrap token is consumed (one of <em>n</em> uses); the agent token
              is written to <Code>/etc/logpulse-agent/agent-token</Code> and used for every subsequent request.
              From there the agent runs autonomously. Start it with{' '}
              <Code>systemctl enable --now logpulse-agent</Code>.
            </P>
            <Callout type="tip">
              Bootstrap tokens have a configurable expiry (default 24h) and a use count (default 100). For
              large fleets, mint a token with{' '}
              <Code>maxUses</Code>=number of hosts and ship it via your provisioning system; the token expires
              by itself once it&apos;s exhausted or the TTL runs out.
            </Callout>
          </SubSection>
        </Section>

        {/* GROUPS */}
        <Section id="groups" title="Groups & inputs">
          <P>
            Each input attached to a group becomes a Vector source in the generated configuration. The
            agent&apos;s platform (<Code>linux</Code> or <Code>kubernetes</Code>) filters which inputs apply.
            A <Code>journald</Code> input is skipped on a Kubernetes pod, a <Code>kubernetes_logs</Code> input
            is skipped on a Linux VM. Inputs can be enabled or disabled per-input without deleting them; a
            disabled input is omitted from the next config.
          </P>
          <P>The input catalog:</P>
          <Table
            headers={['Input', 'Platform', 'What it captures']}
            rows={[
              ['Syslog', 'linux, kubernetes', 'Listen on a UDP/TCP port for RFC 3164 / 5424 syslog messages.'],
              ['File', 'linux, kubernetes', 'Tail one or more files (glob patterns supported).'],
              ['Nginx', 'linux', 'Tail nginx access + error logs, parsed into the Web Access data model (method, url, status, client IP).'],
              ['Journald', 'linux', 'Read systemd journal entries from the host.'],
              ['Docker logs', 'linux', 'Read stdout/stderr of containers on the local Docker daemon.'],
              ['Kubernetes logs', 'kubernetes', 'Read pod logs from the local kubelet (agent in DaemonSet mode).'],
              ['HTTP push', 'linux, kubernetes', 'Listen on an HTTP endpoint for log payloads.'],
              ['Exec', 'linux', 'Run a command on a schedule and capture its output.'],
              ['Socket (TCP/UDP)', 'linux, kubernetes', 'Raw TCP/UDP listener; each delimiter-framed payload is a log line (or parsed as JSON).'],
              ['Splunk HEC', 'linux, kubernetes', 'Act as a Splunk HEC endpoint — existing Splunk forwarders push directly to LogPulse.'],
              ['Fluent forward', 'linux, kubernetes', 'Listen on the Fluent forward protocol (FluentD, Fluent Bit).'],
              ['Kafka consumer', 'linux, kubernetes', 'Consume one or more Kafka topics (plaintext brokers only in v1).'],
              ['Linux audit (auditd)', 'linux', 'Read Linux audit logs. Logins, sudo use and process activity are auto-tagged for security analysis.'],
              ['Host metrics', 'linux', 'CPU, memory, disk, filesystem, load and network metrics from the host, into the metrics store. Query with mstats — see Host monitoring below.'],
              ['Process monitoring', 'linux', 'Periodic snapshot of running processes (top-N by CPU, or all) as agent_process_snapshot log events. See Host monitoring below.'],
            ]}
          />
          <P>
            Per-input <strong>routing overrides</strong> control where events land in LogPulse:
          </P>
          <Table
            headers={['Override', 'Default', 'When to set it']}
            rows={[
              [<Code key="i">indexOverride</Code>, 'main', 'Split prod vs staging, tenants, or applications into separate indexes'],
              [<Code key="s">sourcetypeOverride</Code>, <span key="ss">agent_<em>type</em> (e.g. agent_journald)</span>, 'Tag a specific input with a domain-specific sourcetype (e.g. nginx_access)'],
              [<Code key="r">sourceOverride</Code>, 'logpulse-agent', 'Distinguish multiple inputs of the same type: `source=prod-fw1` vs `source=prod-fw2`'],
            ]}
          />

          <SubSection id="config-version" title="Config versions">
            <P>
              Every edit to a group (add/edit/delete an input, change routing overrides, rename the group)
              increments the group&apos;s <Code>config_version</Code>. Agents only re-fetch the config when their
              own <Code>applied_config_version</Code> falls behind. See{' '}
              <a className="text-primary-600 dark:text-primary-400 hover:underline" href="#config-poll">
                Config polling
              </a>{' '}
              below. The group header shows the rollout: <em>config v7 · applied</em> once every online agent
              runs the latest version, or <em>config v7 · 1/2 agents updated</em> while it is still on its way.
              Agents poll about every 30 seconds, so a change normally lands within a minute.
            </P>
          </SubSection>
        </Section>

        {/* PARSING */}
        <Section id="parsing" title="Parsing">
          <P>
            For common formats the agent parses on the host, with built-in parsers that run automatically: sshd,
            sudo and PAM, nginx and Nginx Proxy Manager, ufw/iptables, Cisco ASA, UniFi, dnsmasq and more. Their
            fields arrive with the event.
          </P>
          <P>
            Everything else is parsed by LogPulse when the event is indexed, by the parsers you build under{' '}
            <strong>Data processing → Parsers</strong>. A parser is scoped to the data (for example{' '}
            <Code>sourcetype=app</Code>), so the same parser covers the sourcetype whether an agent, the HTTP API or
            an integration sent it. The <em>Parse</em> step of a group and the <em>Fields</em> box in each input
            link straight to the builder for that sourcetype. See{' '}
            <Link className="text-primary-600 dark:text-primary-400 hover:underline" to="/docs/data-processing/parsers">
              Parsers
            </Link>.
          </P>
        </Section>

        {/* HOST MONITORING */}
        <Section id="host-monitoring" title="Host monitoring">
          <P>
            Two Linux-only inputs turn an agent host into a lightweight metrics + process source, without
            installing a separate collector: <Code>host_metrics</Code> for resource utilisation and{' '}
            <Code>process_snapshot</Code> for a periodic inventory of running processes.
          </P>

          <SubSection id="host-metrics" title="Host metrics">
            <P>
              <Code>host_metrics</Code> wraps Vector&apos;s native <Code>host_metrics</Code> source. Enable any
              combination of the <Code>cpu</Code>, <Code>memory</Code>, <Code>disk</Code>,{' '}
              <Code>filesystem</Code>, <Code>load</Code> and <Code>network</Code> collectors; the scrape
              interval defaults to 60s. Events don&apos;t go through the log pipeline — they land in the{' '}
              <strong>metrics store</strong> under index <Code>infra</Code>, with OTel-semconv-style metric
              names:
            </P>
            <Table
              headers={['Metric name', 'Collector']}
              rows={[
                [<Code key="cpu">system.cpu.time</Code>, 'cpu'],
                [<Code key="mem">system.memory.usage</Code>, 'memory'],
                [<Code key="fs">system.filesystem.usage</Code>, 'filesystem'],
                [<Code key="load">system.cpu.load_average.1m / .5m / .15m</Code>, 'load'],
                [<Code key="net">system.network.io.receive / .transmit</Code>, 'network'],
                [<Code key="disk">system.disk.io.read / .write</Code>, 'disk'],
              ]}
            />
            <P>Query host metrics with LPQL&apos;s <Code>mstats</Code> command:</P>
            <CodeBlock title="LPQL">{`| mstats avg(value) where index=infra metric_name="system.memory.usage" host="<hostname>" span=5m`}</CodeBlock>
            <Callout type="info">
              <Code>mstats</Code> aggregations always target the <Code>value</Code> column and filter on{' '}
              <Code>metric_name=&quot;…&quot;</Code> — not <Code>avg(&lt;metric name&gt;)</Code>. The metric
              name is a filter, not a column reference.
            </Callout>
            <Callout type="info">
              <strong>Host tag deviation:</strong> the hostname is stored under the bare resource-attribute
              key <Code>host</Code>, not OTel semconv&apos;s <Code>host.name</Code>. This is a deliberate
              choice so <Code>host=</Code> / <Code>by host</Code> in LPQL resolves identically on logs and on
              metrics, without a dotted-key lookup.
            </Callout>
          </SubSection>

          <SubSection id="process-monitoring" title="Process monitoring">
            <P>
              <Code>process_snapshot</Code> runs <Code>logpulse-agent ps-snapshot</Code> via Vector&apos;s{' '}
              <Code>exec</Code> source. Mode <Code>topN</Code> (default 30, ranked by CPU) or <Code>all</Code>{' '}
              captures the full process table. Each snapshot becomes a set of log events, sourcetype{' '}
              <Code>agent_process_snapshot</Code>, mapped onto the canonical <Code>dm-host-process</Code> data
              model: <Code>process_name</Code>, <Code>pid</Code>, <Code>parent_pid</Code>, <Code>user</Code>,{' '}
              <Code>cpu_pct</Code>, <Code>mem_pct</Code>, <Code>rss_bytes</Code>, <Code>state</Code>,{' '}
              <Code>process_cmdline</Code> (truncated to 512 bytes, UTF-8-safe).
            </P>
            <Callout type="warning">
              Snapshots miss short-lived processes — anything that starts and exits between two scrape
              intervals is invisible. For process-execution detection (&quot;did process X run?&quot;), use
              the auditd execve preset below instead.
            </Callout>
            <Callout type="warning">
              Mode <Code>all</Code> captures every process on the host each run. On busy hosts this is a lot
              of events; set the interval to <Code>300s</Code> or higher when using <Code>all</Code>.
            </Callout>
            <Callout type="warning">
              <Code>process_cmdline</Code> is the raw command line. Some tools pass secrets as CLI arguments
              (e.g. <Code>--password=…</Code>); those end up in the snapshot verbatim.
            </Callout>
          </SubSection>

          <SubSection id="execve-preset" title="Auditd execve preset">
            <P>
              For forensically complete process-execution detection — including processes that live for less
              than a second and never show up in a snapshot — add an execve audit rule. In the dashboard, the
              auditd input has a ready-made preset card for this; the underlying ruleset:
            </P>
            <CodeBlock title="/etc/audit/rules.d/logpulse-execve.rules">{`-a always,exit -F arch=b64 -S execve,execveat -k logpulse_exec
-a always,exit -F arch=b32 -S execve,execveat -k logpulse_exec`}</CodeBlock>
            <P>
              Load it with <Code>augenrules --load</Code> (or reboot). Every <Code>execve</Code>/
              <Code>execveat</Code> syscall on the host now produces a <Code>SYSCALL</Code>/<Code>EXECVE</Code>{' '}
              audit record, tagged <Code>key=logpulse_exec</Code>, ingested via the existing auditd input.
            </P>
            <Callout type="warning">
              auditd hex-encodes any argv value that contains a space. Expect <Code>process_cmdline</Code>{' '}
              values like <Code>2F7573722F62696E2F6375726C</Code> for arguments with spaces, alongside plain
              text for single-token arguments.
            </Callout>
            <Callout type="warning">
              This preset generates significant volume on busy build hosts (CI runners, package builders) —
              every process spawn is an event. Scope the rule or route it to a lower-retention index if
              that&apos;s a concern.
            </Callout>
            <Callout type="info">
              On <Code>SYSCALL</Code>/<Code>EXECVE</Code> records, <Code>user_id</Code> carries the audit
              login identity (<Code>auid</Code>) when the kernel has one set, taking precedence over the
              effective <Code>uid</Code>. <Code>auid</Code> is assigned at login and doesn&apos;t change on{' '}
              <Code>sudo</Code>/<Code>su</Code>, so this attributes execve events to the human who logged in
              rather than the account the command executed as.
            </Callout>
          </SubSection>
        </Section>

        {/* PROTOCOL */}
        <Section id="protocol" title="Control-plane protocol">
          <P>
            Once enrolled, an agent only talks to LogPulse over HTTPS. Three endpoints make up the entire
            agent-side surface area: enrolment (once), config polling (on a tight loop), and heartbeats
            (slower loop, also reports action results). LogPulse never opens a connection back to the agent.
          </P>

          <SubSection id="enrollment-api" title="Enrolment: POST /api/v1/agents/enrol">
            <P>
              Called once during install. Request body authenticates with the short-lived enrolment (bootstrap)
              token; response includes the agent&apos;s long-lived bearer token.
            </P>
            <CodeBlock title="POST /api/v1/agents/enrol">{`{
  "bootstrapToken": "EBT_<bootstrap-token>",
  "hostname": "fw1.prod.example.com",
  "platform": "linux",
  "os": "Ubuntu 24.04",
  "kernel": "6.8.0-35-generic",
  "agentVersion": "0.4.1",
  "vectorVersion": "0.40.0",
  "groupSlug": "wakanda-enterprise"   // optional: overrides the token's default group
}`}</CodeBlock>
            <P>The response carries an <Code>AGT_</Code> bearer token, shown once, stored hashed (SHA-256) at rest:</P>
            <CodeBlock title="201 Created">{`{
  "data": {
    "agentId": "f1c4…",
    "agentToken": "AGT_<32-byte base64url>",
    "groupId": "6d0e…",
    "configVersion": 7
  }
}`}</CodeBlock>
            <Callout type="warning">
              <Code>AGT_</Code> tokens are credentials. They&apos;re written to{' '}
              <Code>/etc/logpulse-agent/agent-token</Code> with mode 0600 owned by the <Code>logpulse</Code>{' '}
              user. Revoke a stolen or stale agent from the <em>Agents</em> tab; the next request from that
              token returns 403.
            </Callout>
          </SubSection>

          <SubSection id="config-poll" title="Config polling: GET /api/v1/agents/:id/config">
            <P>
              The agent polls this endpoint at a short interval (~10s by default). It passes its currently
              applied version via the <Code>since</Code> query parameter; LogPulse short-circuits with{' '}
              <Code>204 No Content</Code> when nothing has changed.
            </P>
            <CodeBlock title="GET /api/v1/agents/f1c4.../config?since=7">{`Authorization: Bearer AGT_<token>

→ 204 No Content      # since >= group.configVersion, nothing to apply
→ 200 OK              # new config available
  Content-Type: application/yaml
  x-config-version: 8
  x-config-etag: sha256:9f8…
  Body: <Vector YAML>`}</CodeBlock>
            <P>
              The body is the same configuration the dashboard shows under <em>Config (YAML)</em> on the group page.
              Agents write it to <Code>/var/lib/logpulse-agent/vector.yaml</Code>, validate it with{' '}
              <Code>vector validate</Code>, and reload Vector on success. A failed validation aborts the apply.
              The agent keeps running the previous config and surfaces the error in its next heartbeat.
            </P>
            <P>
              When the group has no enabled inputs the response body is the literal string <Code>{'{}'}</Code>.
              The agent detects this and stops Vector entirely (no placeholder pipeline, no wasted RAM).
            </P>
          </SubSection>

          <SubSection id="heartbeats" title="Heartbeats: POST /api/v1/agents/:id/heartbeat">
            <P>
              Heartbeats are how an agent says &quot;I&apos;m alive&quot;, reports its current state, and asks
              for queued actions. The default cadence is 30 seconds. The body is small but carries every field
              the dashboard needs to render the agents table:
            </P>
            <CodeBlock title="POST /api/v1/agents/f1c4.../heartbeat">{`{
  "agentVersion": "0.4.1",
  "vectorVersion": "0.40.0",
  "appliedConfigVersion": 8,
  "vectorRunning": true,
  "vectorUptimeSeconds": 1837,
  "actionResults": [          // optional: results for previously dispatched actions
    { "actionId": "...", "status": "done", "result": { "previousVersion": "0.4.0" } }
  ]
}`}</CodeBlock>
            <P>The server updates these columns on <Code>agent_registrations</Code>:</P>
            <Table
              headers={['Column', 'Source', 'Used for']}
              rows={[
                [<Code key="lh">last_heartbeat_at</Code>, 'now()', 'Online/offline badge (5-min threshold)'],
                [<Code key="av">agent_version</Code>, 'body.agentVersion', 'Per-host upgrade decisions'],
                [<Code key="vv">vector_version</Code>, 'body.vectorVersion', 'Vector-version sweep, troubleshooting'],
                [<Code key="ac">applied_config_version</Code>, 'body.appliedConfigVersion', 'Rollout progress badge'],
                [<Code key="vr">vector_running</Code>, 'body.vectorRunning', 'Surface failed configs in the UI'],
                [<Code key="vs">vector_started_at</Code>, 'derived from uptime', '"Running for 2h 14m" indicator'],
                [<Code key="wo">went_offline_at</Code>, 'cleared on recovery', 'Distinguishes brief flaps from extended outages'],
              ]}
            />
            <P>
              An agent is considered <strong>offline</strong> when its <Code>last_heartbeat_at</Code> is older
              than the constant <Code>AGENT_OFFLINE_THRESHOLD_MS</Code> = 5 minutes (defined in{' '}
              <Code>packages/types</Code>). The dashboard renders an &quot;Offline&quot; badge accordingly.
            </P>
            <P>
              The response carries any queued actions for this agent. See below.
            </P>
            <CodeBlock title="200 OK">{`{
  "data": {
    "pendingActions": [
      {
        "id": "...",
        "type": "upgrade_agent",
        "payload": { "targetVersion": "0.4.2" }
      }
    ]
  }
}`}</CodeBlock>
          </SubSection>

          <SubSection id="actions" title="Remote actions">
            <P>
              Actions are a generic queue for &quot;do something on the agent&quot; instructions. The current
              type is <Code>upgrade_agent</Code>; future types (restart Vector, drop cache, rotate token) plug
              into the same machinery. Status flows in one direction only:
            </P>
            <Table
              headers={['Status', 'Set by', 'Meaning']}
              rows={[
                ['pending', 'Server (created)', 'Queued, not yet handed to an agent'],
                ['running', 'Agent (via actionResults)', 'Agent has picked it up and is executing'],
                ['done', 'Agent', 'Action completed successfully. Result payload available'],
                ['failed', 'Agent', 'Action failed. Error message in result'],
                ['cancelled', 'Server (operator)', 'Action was cancelled before dispatch'],
                ['expired', 'Server (sweep)', 'Action wasn’t picked up before its deadline'],
              ]}
            />
            <P>
              The server validates state transitions: an action that&apos;s already <Code>done</Code> can&apos;t
              go back to <Code>running</Code>; an <Code>expired</Code> action stays expired. Agents only
              receive non-expired, non-cancelled actions in the <Code>pendingActions</Code> array (up to 10 per
              heartbeat).
            </P>
            <Callout type="info">
              Action results are reported via <em>the next heartbeat after execution finishes</em>. There is no
              dedicated webhook back to LogPulse. This keeps the agent-side network topology trivially simple:
              outbound HTTPS only, no inbound port, no callback URL.
            </Callout>
          </SubSection>
        </Section>

        {/* UPGRADES */}
        <Section id="upgrades" title="Remote upgrades">
          <P>
            Selecting an agent in the dashboard and clicking <em>Upgrade</em> creates an{' '}
            <Code>upgrade_agent</Code> action with the target version in its payload. The action waits in{' '}
            <Code>pending</Code> until the agent&apos;s next heartbeat, then flows through the state machine:
          </P>
          <CodeBlock title="Lifecycle of an upgrade">{`1. Operator clicks Upgrade → POST /api/v1/agents/:id/actions
   { type: "upgrade_agent", payload: { targetVersion: "0.4.2" } }
   Status: pending

2. Next heartbeat from the agent (≤ 30s later):
   Server returns pendingActions = [the action]
   Server transitions: pending → running (when the agent acks)

3. Agent runs the install script with the pinned version:
   curl -fsSL https://get.logpulse.io | sudo LOGPULSE_VERSION=v0.4.2 sh
   The new binary replaces the old one, systemd restarts the unit, the
   new process loads its existing token from /etc/logpulse-agent.

4. The first heartbeat from the upgraded agent carries actionResults:
   { actionId, status: "done", result: { fromVersion: "0.4.1", toVersion: "0.4.2" } }
   Server transitions: running → done`}</CodeBlock>
          <P>
            Bulk upgrades use the same machinery. The dashboard issues one action per selected agent and
            shows aggregate progress as results trickle in. Vector itself isn&apos;t upgraded by this action;
            Vector lives under <Code>/opt/logpulse-agent/vector/</Code> and rolls forward with the agent
            release that bundles it.
          </P>
          <Callout type="warning">
            Upgrades that take longer than the action&apos;s configured deadline are marked{' '}
            <Code>expired</Code> by the server sweep. The upgrade may still have succeeded on the host. Check
            the agent&apos;s next heartbeat for the actual <Code>agentVersion</Code> rather than relying on the
            action status alone.
          </Callout>
        </Section>

        {/* TROUBLESHOOTING */}
        <Section id="troubleshooting" title="Troubleshooting">
          <SubSection id="t-token" title="Agent returns 403 on every request">
            <P>
              The bearer token was revoked from the <em>Agents</em> tab, or it doesn&apos;t match the prefix on
              the server side. Mint a fresh bootstrap token, run{' '}
              <Code>logpulse-agent enrol</Code> again, and restart the unit.
            </P>
          </SubSection>
          <SubSection id="t-config" title="Config changes don't reach an agent">
            <P>
              Check the agent&apos;s row in the dashboard. If <Code>applied_config_version</Code> stays behind{' '}
              <Code>config_version</Code> after a couple of polls, the agent likely failed validation. Open
              the agent logs (<Code>journalctl -u logpulse-agent</Code>) and look for{' '}
              <Code>vector validate</Code> errors. The agent keeps the previous config running until a new one
              passes validation.
            </P>
          </SubSection>
          <SubSection id="t-offline" title="Agent shows Offline but the host is up">
            <P>
              The agent hasn&apos;t sent a heartbeat in over 5 minutes. Common causes: outbound HTTPS to{' '}
              <Code>api.logpulse.io</Code> is blocked by a firewall, the agent process crashed (
              <Code>systemctl status logpulse-agent</Code>), or system clock skew is rejecting TLS. The
              dashboard tooltip on the Offline badge shows the exact <em>last seen</em> timestamp.
            </P>
          </SubSection>
          <SubSection id="t-upgrade" title="Upgrade action stuck in running">
            <P>
              The upgrade started but never reported a result. Check the agent&apos;s{' '}
              <Code>agent_version</Code> in the next heartbeat. If it matches the target, the upgrade
              succeeded but the result-reporting heartbeat got lost; the action will expire by itself. If the
              version is unchanged, look at the install script&apos;s output in{' '}
              <Code>/var/log/logpulse-agent/upgrade.log</Code>.
            </P>
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
